import { useState, useEffect } from "react";

// Import utility functions for generating unique IDs
import { v4 as uuidv4 } from "uuid";

// Import necessary components and styles
import { Empty, Skeleton, Button, Input } from "antd";

import EBulletinCarousel from "../../components/EBulletin/EBulletinCarousel";
import AddSectionCard from "../../components/EBulletin/EbulletinAddSection";
import SectionCard from "../../components/EBulletin/cards/SectionCard";
import EventsAdvisoriesPanel from "../../components/EBulletin/EventsAdvisoriesPanel";
import { useBulletinSectionService, useBulletinService } from "../../services";

import { useSelector } from "react-redux";

import "../../styles/bulletinAdmin/components/bulletinContent.scss";

export default function EBulletinContent({ isAdmin, onBulletinUpdate }) {
  const [sections, setSections] = useState([]);
  const [currentBulletin, setCurrentBulletin] = useState(null);
  const isReordering = false;
  const [searchQuery, setSearchQuery] = useState("");

  const { ebullCurrentTab } = useSelector((state) => state.generalSlice);

  const {
    service: sectionService,
    response: sectionResponse,
    loading: sectionLoading,
  } = useBulletinSectionService();

  const { service: bulletinService } = useBulletinService();

  // Update current bulletin when the bulletin prop changes
  // useEffect(() => {
  //   setCurrentBulletin(bulletin);
  // }, [bulletin]);

  // // Fetch sections when bulletinId changes
  // useEffect(() => {
  //   const fetchSections = async () => {
  //     if (!bulletinId) return;
  //     try {
  //       await sectionService.getAllSections(bulletinId);
  //     } catch (err) {
  //       console.error("Failed to fetch sections:", err);
  //     }
  //   };
  //   fetchSections();
  // }, [bulletinId]);

  // Fetch the bulletin meta/details whenever activeId changes
  useEffect(() => {
    console.log("1");

    let mounted = true;
    const run = async () => {
      if (!ebullCurrentTab) {
        setCurrentBulletin(null);
        setSections([]);
        return;
      }
      try {
        const res = await bulletinService.getBulletin(ebullCurrentTab);
        const data = res?.data ?? res ?? null;
        if (mounted) setCurrentBulletin(data || null);
      } catch (e) {
        console.error("Failed to load bulletin:", e);
        if (mounted) setCurrentBulletin(null);
      }
    };
    run();
    return () => (mounted = false);
  }, [ebullCurrentTab]);

  // Fetch sections whenever activeId changes
  useEffect(() => {
    const fetchSections = async () => {
      if (!ebullCurrentTab) return;
      try {
        await sectionService.getAllSections(ebullCurrentTab);
      } catch (err) {
        console.error("Failed to fetch sections:", err);
      }
    };
    fetchSections();
  }, [ebullCurrentTab]);

  console.log(sectionResponse);

  // Update sections when response changes
  // useEffect(() => {
  //   if (response?.data) {
  //     const rawData = Array.isArray(response.data) ? response.data : [];
  //     const normalized = rawData.map((section) => ({
  //       ...section,
  //       title: section.section_title || "",
  //       blocks: Array.isArray(section.content)
  //         ? section.content.map((block) => ({
  //             id: block.id,
  //             type: block.type,
  //             content: block.content,
  //             display_order: block.display_order,
  //             is_active: block.is_active,
  //             contentId: block.id,
  //           }))
  //         : [],
  //     }));
  //     setSections(normalized);
  //   } else if (!loading) {
  //     setSections([]);
  //   }
  // }, [response, loading]);

  // The sections API stores media block data (url, alt, caption, style) as a
  // serialized JSON string in the `content` field rather than top-level fields.
  // Parse it so those properties are available directly on the block object;
  // without this, re-saving overwrites the stored URL with an empty string.
  const parseMediaContent = (block) => {
    if (!block || !["image", "video", "file"].includes(block.type) || !block.content) {
      return block;
    }
    try {
      const parsed = JSON.parse(block.content);
      if (parsed && typeof parsed === "object") return { ...block, ...parsed };
    } catch (e) { /* not JSON */ }
    return block;
  };

  useEffect(() => {
    if (Array.isArray(sectionResponse?.data)) {
      const normalized = sectionResponse.data.map((section) => ({
        ...section,
        title: section.section_title || "",
        blocks: Array.isArray(section.content)
          ? section.content.map((block) => {
              const base = {
                ...block,
                id: block.id,
                type: block.type,
                content: block.content,
                display_order: block.display_order,
                is_active: block.is_active,
                contentId: block.id,
              };

              // Parse media content for top-level blocks
              if (["image", "video", "file"].includes(block.type) && block.content) {
                try {
                  const parsed = JSON.parse(block.content);
                  if (parsed && typeof parsed === "object") return { ...base, ...parsed };
                } catch (e) { /* not JSON, leave as-is */ }
              }

              // Parse media content inside multi-column child blocks
              if (block.type === "two-column") {
                return {
                  ...base,
                  leftBlock: parseMediaContent(base.leftBlock),
                  rightBlock: parseMediaContent(base.rightBlock),
                };
              }

              if (block.type === "three-column") {
                return {
                  ...base,
                  leftBlock: parseMediaContent(base.leftBlock),
                  centerBlock: parseMediaContent(base.centerBlock),
                  rightBlock: parseMediaContent(base.rightBlock),
                };
              }

              return base;
            })
          : [],
      }));
      setSections(normalized);
    } else if (!sectionLoading) {
      setSections([]);
    }
  }, [sectionResponse, sectionLoading]);

  // Function to add a new section
  const onAddSection = () => {
    if (!isAdmin) return;
    const tempId = uuidv4();
    const blockId = uuidv4();

    setSections((prev) => [
      ...prev,
      {
        tempId,
        id: null,
        title: "",
        display_order: prev.length,
        blocks: [
          {
            id: blockId,
            type: "text",
            content: "",
          },
        ],
        isEditing: true,
      },
    ]);
  };

  const reloadSections = async () => {
    if (!ebullCurrentTab) return;
    try {
      await sectionService.getAllSections(ebullCurrentTab);
    } catch (err) {
      console.error("Failed to reload sections:", err);
    }
  };

  const reloadBulletin = async () => {
    if (!ebullCurrentTab) return;
    try {
      const response = await bulletinService.getBulletin(ebullCurrentTab);
      const updatedBulletin = response?.data ?? response ?? null;
      if (updatedBulletin) {
        setCurrentBulletin(updatedBulletin);
        onBulletinUpdate?.(updatedBulletin);
      }
    } catch (err) {
      console.error("Failed to reload bulletin:", err);
    }
  };

  // Filter sections based on search query and admin status
  const displayableSections = sections
    .filter((section) =>
      section.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((section) => (isAdmin ? true : !section.isEditing));

  const isCommunityUpdates =
    currentBulletin?.title?.trim().toLowerCase() === "community updates";

  return (
    <div>
      <EBulletinCarousel
        bulletin={currentBulletin}
        isAdmin={isAdmin}
        reloadBulletin={reloadBulletin}
      />

      {isCommunityUpdates ? (
        /* ── Community Updates: Events & Advisories only ── */
        <EventsAdvisoriesPanel bulletinId={ebullCurrentTab} isAdmin={isAdmin} />
      ) : (
        /* ── All other bulletins: free-form sections ── */
        <div>
          {sectionLoading ? (
            <div>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ) : (
            <>
              <div className="page-header">
                <Input.Search
                  placeholder="Search sections by title..."
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                />
              </div>

              {displayableSections.length === 0 ? (
                <div style={{ padding: "24px", textAlign: "center" }}>
                  <Empty
                    description={
                      searchQuery
                        ? "No sections match your search."
                        : "This bulletin has no content yet."
                    }
                  />
                </div>
              ) : (
                displayableSections.map((section, index) => (
                  <SectionCard
                    key={section.id || section.tempId}
                    id={section.id || section.tempId}
                    section={section}
                    sectionIdx={index}
                    sections={sections}
                    setSections={setSections}
                    bulletinId={ebullCurrentTab}
                    reloadSections={reloadSections}
                    isAdmin={isAdmin}
                    isReordering={false}
                  />
                ))
              )}
            </>
          )}

          {isAdmin && !sectionLoading && !isReordering && (
            <div style={{ paddingBottom: "24px" }}>
              <AddSectionCard onClick={onAddSection} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
